<script lang="ts">
  /**
   * MobileTaskList — the Timeline "Tasks" board on phones (plans/mobile-v2.md §3.1):
   * the kanban's three lanes become status segments (To Do · 📌 Working on · Done),
   * the filters become a chip row whose chips open bottom-sheet pickers, and the
   * group-by becomes list sections.
   *
   * Presentational: `TaskKanbanView` keeps the data and the preferences (fetching,
   * the task cache, search / priority / project filters, group-by) and passes the
   * lanes it derived with `lib/taskBoard.ts`, so counts and filters are the desktop
   * board's. This component only owns view state: the selected segment, which sheet
   * is open and which row is swiped open.
   *
   * - Default segment: Working on when it has tasks, else To Do (chosen once, when
   *   the list mounts, so unpinning the last task doesn't jump segments).
   * - Rows: tap the title → the task sheet; the checkbox completes / reopens; swipe
   *   left for Pin / Unpin and Done ✓ (both are also in the task sheet).
   * - "+" adds a task; the sheet has a project picker.
   */
  import type { Project, TaskPriority } from '../../lib/types';
  import { categoryIcons } from '../../lib/utils';
  import { highlightSegments } from '../../lib/searchHighlight';
  import {
    PRIORITIES,
    defaultBoardLane,
    priorityDotColors,
    priorityLabels,
    type BoardLane,
    type GroupBy,
    type TaskGroup,
    type TaskWithProject,
  } from '../../lib/taskBoard';
  import ChipRow, { type Chip } from './ChipRow.svelte';
  import FabButton from './FabButton.svelte';
  import MobileAddTask, { type NewTaskDraft } from './MobileAddTask.svelte';
  import OptionSheet from './OptionSheet.svelte';
  import SegmentedControl from './SegmentedControl.svelte';
  import SwipeRow, { type SwipeAction } from './SwipeRow.svelte';
  import Icon from '../Icon.svelte';

  interface Props {
    groups: Record<BoardLane, TaskGroup[]>;
    counts: Record<BoardLane, number>;
    /** The projects the period shows: the project filter's and the add sheet's options. */
    projects: Project[];
    searchQuery?: string;
    groupBy: GroupBy;
    priorityFilter: ReadonlySet<TaskPriority>;
    projectFilter: ReadonlySet<string>;
    onGroupByChange: (groupBy: GroupBy) => void;
    onPriorityFilterChange: (priorities: Set<TaskPriority>) => void;
    onProjectFilterChange: (projectIds: Set<string>) => void;
    onClearFilters: () => void;
    onOpenTask: (task: TaskWithProject, anchorEl: HTMLElement) => void;
    /** Completes an open task, reopens a done one. */
    onComplete: (task: TaskWithProject) => void;
    onTogglePin: (task: TaskWithProject) => void;
    onAddTask: (draft: NewTaskDraft & { projectId: string }) => void;
  }

  let {
    groups,
    counts,
    projects,
    searchQuery = $bindable(''),
    groupBy,
    priorityFilter,
    projectFilter,
    onGroupByChange,
    onPriorityFilterChange,
    onProjectFilterChange,
    onClearFilters,
    onOpenTask,
    onComplete,
    onTogglePin,
    onAddTask,
  }: Props = $props();

  // svelte-ignore state_referenced_locally
  let segment = $state<BoardLane>(defaultBoardLane(counts));
  let sheet = $state<'group' | 'priority' | 'project' | null>(null);
  let addOpen = $state(false);
  let swipedId = $state<string | null>(null);

  // The flat list ('All') is always one section, empty or not: no section, no rows.
  let sections = $derived(groups[segment].filter((group) => group.tasks.length > 0));
  let hasFilters = $derived(
    searchQuery.trim() !== '' || priorityFilter.size > 0 || projectFilter.size > 0,
  );
  let query = $derived(searchQuery.trim());
  let sortedProjects = $derived([...projects].sort((a, b) => a.title.localeCompare(b.title)));
  let defaultProjectId = $derived(projectFilter.size === 1 ? [...projectFilter][0] : null);

  const groupLabels: Record<GroupBy, string> = {
    none: 'All',
    priority: 'Priority',
    project: 'Project',
  };

  let segmentOptions = $derived([
    { value: 'todo', label: 'To Do', count: counts.todo },
    { value: 'active', label: 'Working on', icon: '📌', count: counts.active },
    { value: 'done', label: 'Done', count: counts.done },
  ]);

  let chips = $derived<Chip[]>([
    {
      id: 'group',
      label: `Group: ${groupLabels[groupBy]}`,
      active: groupBy !== 'none',
      caret: true,
      onSelect: () => (sheet = 'group'),
    },
    {
      id: 'priority',
      label: priorityFilter.size === 0 ? 'All priorities' : `Priority (${priorityFilter.size})`,
      active: priorityFilter.size > 0,
      caret: true,
      onSelect: () => (sheet = 'priority'),
    },
    {
      id: 'project',
      label: projectFilter.size === 0 ? 'All projects' : `Projects (${projectFilter.size})`,
      active: projectFilter.size > 0,
      caret: true,
      onSelect: () => (sheet = 'project'),
    },
  ]);

  function swipeActions(task: TaskWithProject): SwipeAction[] {
    return [
      {
        label: task.isActive ? 'Unpin' : 'Pin',
        color: '#f59e0b',
        onSelect: () => onTogglePin(task),
      },
      { label: 'Done ✓', color: '#22c55e', onSelect: () => onComplete(task) },
    ];
  }

  function submitTask(draft: NewTaskDraft) {
    if (!draft.projectId) return;
    // A new task is open and not pinned: show the segment it lands in.
    segment = 'todo';
    onAddTask({ ...draft, projectId: draft.projectId });
  }

  // One swiped-open row at a time: a press anywhere else, or scrolling, closes it.
  // Like an iOS list, that press only closes the row: the tap it starts is swallowed
  // (not passed on to another row, which would open its sheet).
  let swallowTap = false;
  let swallowTimer: ReturnType<typeof setTimeout> | undefined;

  function stopSwallowing() {
    swallowTap = false;
    clearTimeout(swallowTimer);
  }

  function closeSwipeOnOutsidePress(event: PointerEvent) {
    if (swipedId && !(event.target as Element).closest('[data-swipe-open="true"]')) {
      swipedId = null;
      swallowTap = true;
      // No click follows when the press turns into a scroll (pointercancel) or lingers.
      clearTimeout(swallowTimer);
      swallowTimer = setTimeout(stopSwallowing, 1000);
    }
  }

  function swallowTapAfterClose(event: MouseEvent) {
    if (!swallowTap) return;
    stopSwallowing();
    event.preventDefault();
    event.stopPropagation();
  }

  function isDone(task: TaskWithProject) {
    return task.status !== 'todo';
  }

  const emptyText: Record<BoardLane, string> = {
    todo: 'No tasks to do',
    active: 'Nothing pinned yet. Swipe a task left and tap Pin.',
    done: 'No completed tasks yet',
  };
</script>

{#snippet taskRow(task: TaskWithProject)}
  {@const done = isDone(task)}
  {@const hasMeta = task.priority !== 'none' || groupBy !== 'project' || !!task.notes}
  <div class="row" class:done class:wont-do={task.status === 'wont_do'}>
    <button
      type="button"
      class="check"
      aria-label={done ? `Reopen ${task.title}` : `Complete ${task.title}`}
      onclick={() => onComplete(task)}
    >
      <span
        class="box"
        class:checked={done}
        style="border-color: {done ? '' : priorityDotColors[task.priority]};"
        >{#if task.status === 'wont_do'}✕{:else if done}✓{/if}</span
      >
    </button>
    <button type="button" class="body" onclick={(event) => onOpenTask(task, event.currentTarget)}>
      <span class="title"
        >{#each highlightSegments(task.title, query ? [query] : []) as seg, i (i)}{#if seg.highlighted}<mark
              class="hit">{seg.text}</mark
            >{:else}{seg.text}{/if}{/each}</span
      >
      {#if hasMeta}
        <span class="meta">
          {#if task.priority !== 'none'}
            <span class="prio">
              <span class="dot" style="background: {priorityDotColors[task.priority]};"></span>
              {priorityLabels[task.priority]}
            </span>
          {/if}
          {#if groupBy !== 'project'}
            <span class="project">
              {categoryIcons[task.project.category] || '📋'}
              {task.project.title}
            </span>
          {/if}
          {#if task.notes}<span class="notes">📝 Notes</span>{/if}
        </span>
      {/if}
    </button>
    {#if task.isActive && !done}
      <span class="pin" role="img" aria-label="Working on">📌</span>
    {/if}
  </div>
{/snippet}

<svelte:window onpointerdowncapture={closeSwipeOnOutsidePress} onpointercancel={stopSwallowing} />

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="mtl" onclickcapture={swallowTapAfterClose}>
  <div class="head">
    <div class="segments">
      <SegmentedControl
        options={segmentOptions}
        value={segment}
        label="Task status"
        onChange={(value) => (segment = value as BoardLane)}
      />
    </div>

    <div class="search">
      <Icon name="search" size={18} />
      <input
        type="text"
        autocomplete="off"
        enterkeyhint="search"
        class="search-input"
        placeholder="Search tasks…"
        aria-label="Search tasks"
        bind:value={searchQuery}
        onkeydown={(e) => {
          if (e.key === 'Escape') searchQuery = '';
        }}
      />
      {#if searchQuery !== ''}
        <button
          type="button"
          class="search-clear"
          aria-label="Clear search"
          onclick={() => (searchQuery = '')}><Icon name="close" size={16} /></button
        >
      {/if}
    </div>

    <ChipRow {chips} label="Filters" />
  </div>

  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="list" onscroll={() => (swipedId = null)}>
    {#each sections as group (group.key)}
      <section class="section" aria-label={group.label || undefined}>
        {#if group.label}
          <h2 class="section-h">
            {#if group.priorityKey}
              <span class="dot" style="background: {priorityDotColors[group.priorityKey]};"></span>
            {/if}
            <span class="section-title">{group.label}</span>
            <span class="section-count">{group.tasks.length}</span>
          </h2>
        {/if}
        <ul class="rows">
          {#each group.tasks as task (task.id)}
            <li>
              {#if isDone(task)}
                {@render taskRow(task)}
              {:else}
                <SwipeRow
                  actions={swipeActions(task)}
                  open={swipedId === task.id}
                  onOpenChange={(open) => (swipedId = open ? task.id : null)}
                >
                  {@render taskRow(task)}
                </SwipeRow>
              {/if}
            </li>
          {/each}
        </ul>
      </section>
    {:else}
      <div class="empty">
        <p>{hasFilters ? 'No tasks match your filters' : emptyText[segment]}</p>
        {#if hasFilters}
          <button type="button" class="empty-btn" onclick={onClearFilters}>Clear filters</button>
        {/if}
      </div>
    {/each}
  </div>

  <!-- Without projects (an empty period) the add sheet couldn't submit -->
  {#if projects.length > 0}
    <FabButton label="Add task" onClick={() => (addOpen = true)} />
  {/if}
</div>

<MobileAddTask
  trigger={false}
  bind:open={addOpen}
  {defaultProjectId}
  projects={sortedProjects}
  onSubmit={submitTask}
/>

<OptionSheet
  open={sheet === 'group'}
  onClose={() => (sheet = null)}
  title="Group by"
  options={[
    { value: 'none', label: 'All (no grouping)' },
    { value: 'priority', label: 'Priority' },
    { value: 'project', label: 'Project' },
  ]}
  selected={[groupBy]}
  onChange={([value]) => onGroupByChange(value as GroupBy)}
/>

<OptionSheet
  open={sheet === 'priority'}
  onClose={() => (sheet = null)}
  title="Priority"
  multiple
  options={PRIORITIES.map((p) => ({
    value: p,
    label: priorityLabels[p],
    dot: priorityDotColors[p],
  }))}
  selected={[...priorityFilter]}
  onChange={(values) => onPriorityFilterChange(new Set(values as TaskPriority[]))}
/>

<OptionSheet
  open={sheet === 'project'}
  onClose={() => (sheet = null)}
  title="Projects"
  multiple
  searchable
  searchPlaceholder="Search projects…"
  emptyText="No projects match"
  options={sortedProjects.map((p) => ({
    value: p.id,
    label: p.title,
    icon: categoryIcons[p.category] || '📋',
  }))}
  selected={[...projectFilter]}
  onChange={(values) => onProjectFilterChange(new Set(values))}
/>

<style>
  .mtl {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    background: #f8fafc;
  }

  .head {
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 0 2px;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
  }

  .segments {
    display: flex;
    padding: 0 12px;
  }

  .search {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 44px;
    margin: 4px 12px 0;
    padding: 0 4px 0 12px;
    color: #6b7280;
    background: #f1f5f9;
    border-radius: 10px;
  }

  .search:focus-within {
    box-shadow: 0 0 0 2px #93c5fd;
  }

  .search-input {
    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0;
    color: #111827;
    font: inherit;
    font-size: 16px;
    background: transparent;
    border: 0;
    outline: none;
  }

  .search-input::placeholder {
    color: #6b7280;
  }

  .search-clear {
    display: grid;
    place-items: center;
    flex: none;
    width: 44px;
    height: 44px;
    padding: 0;
    color: #6b7280;
    background: none;
    border: 0;
    cursor: pointer;
  }

  .list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    /* room for the "+" button below the last row */
    padding-bottom: calc(56px + 32px);
  }

  .section-h {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    padding: 10px 14px 6px;
    color: #374151;
    font-size: 13px;
    font-weight: 700;
    background: #f8fafc;
  }

  .section-title {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .section-count {
    color: #9ca3af;
    font-weight: 500;
  }

  .dot {
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .rows {
    margin: 0;
    padding: 0;
    list-style: none;
    background: #fff;
    border-top: 1px solid #eef0f3;
  }

  .rows > li + li {
    border-top: 1px solid #f1f3f5;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 2px;
    min-height: 56px;
    padding: 0 12px 0 4px;
    background: #fff;
  }

  .check {
    display: grid;
    place-items: center;
    flex: none;
    width: 44px;
    height: 44px;
    padding: 0;
    background: none;
    border: 0;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .box {
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    background: #fff;
    border: 2px solid #d1d5db;
    border-radius: 6px;
  }

  .box.checked {
    background: #22c55e;
    border-color: #22c55e;
  }

  .row.wont-do .box.checked {
    background: #9ca3af;
    border-color: #9ca3af;
  }

  .body {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    gap: 3px;
    min-width: 0;
    min-height: 56px;
    padding: 8px 0;
    font: inherit;
    text-align: left;
    background: none;
    border: 0;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .title {
    display: -webkit-box;
    overflow: hidden;
    color: #111827;
    font-size: 16px;
    line-height: 1.3;
    overflow-wrap: anywhere;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .row.done .title {
    color: #9ca3af;
    text-decoration: line-through;
  }

  .hit {
    color: inherit;
    background: #fef08a;
    border-radius: 2px;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    color: #6b7280;
    font-size: 12.5px;
    line-height: 1.2;
    white-space: nowrap;
  }

  .prio {
    display: inline-flex;
    flex: none;
    align-items: center;
    gap: 5px;
  }

  .project {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .notes {
    flex: none;
  }

  .pin {
    flex: none;
    padding-left: 4px;
    font-size: 15px;
  }

  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 40px 24px;
    color: #6b7280;
    text-align: center;
  }

  .empty p {
    margin: 0;
  }

  .empty-btn {
    min-height: 44px;
    padding: 0 16px;
    color: #2563eb;
    font: inherit;
    font-weight: 600;
    background: none;
    border: 0;
    cursor: pointer;
  }

  .body:focus-visible,
  .check:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: -2px;
  }
</style>
